import React, { useState } from "react";
import {
  FlagProvider,
  InMemoryStorageProvider,
  UnleashClient,
} from "@unleash/proxy-client-react";

type MockedFeatureFlagProviderProps = {
  children: React.ReactNode;
  flags?: Record<string, boolean>;
};

function createMockClient(flags: Record<string, boolean> | undefined) {
  const client = new UnleashClient({
    url: "http://localhost/api/flags",
    clientKey: "testsMock",
    appName: "testsMock",
    refreshInterval: 0,
    disableMetrics: true,
    disableRefresh: true,
    bootstrapOverride: true,
    // localStorage would leak toggles across tests; an empty bootstrap can't override them
    storageProvider: new InMemoryStorageProvider(),
    // unleash needs fetch to be defined (or present in the env) or it will throw an error
    // this mock fetch always returns 304, which makes the client behave correctly
    fetch: () =>
      Promise.resolve({
        status: 304,
      }),
    bootstrap: Object.entries(flags ?? {}).map(([key, enabled]) => ({
      name: key,
      enabled,
      variant: {
        name: key,
        enabled,
      },
      impressionData: false,
    })),
  });
  // client should be treated as ready in tests and we'd rather do this here than in the production code
  jest.spyOn(client, "isReady").mockReturnValue(true);

  return client;
}

export function MockedFeatureFlagProvider({ children, flags }: MockedFeatureFlagProviderProps) {
  // FlagProvider only uses the client from its first render, so build one per mount
  const [client] = useState(() => createMockClient(flags));

  return <FlagProvider unleashClient={client}>{children}</FlagProvider>;
}
